import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Col, Row, View } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import React, { useEffect, useRef, useState } from 'react';
import { ExceptionDetail } from 'src/models/ExceptionDetail';
import { FailureContext } from 'src/utils/ErrorRedirectUtils';
import './ErrorDetailsPanel.scss';

const SHOW_LABEL = 'Show error details';
const HIDE_LABEL = 'Hide error details';
const COPY_LABEL = 'Copy details';
const COPY_DONE_LABEL = 'Copied';

export interface ErrorDetailsPanelProps {
  /** The parsed API exception whose fields are shown as engineer detail rows. */
  exception?: ExceptionDetail;
  /**
   * Optional page/operation context (source/api/sessionId/timestamp/component
   * stack). Accepts a partial so inline/modal callers can pass just `source`.
   */
  context?: Partial<FailureContext>;
  /** Fallback error code shown when the exception carries none. */
  errorCode?: string;
  /**
   * Whether to include the raw backend `responseBody` row. Defaults to true:
   * PXT CMS/Catalyst APIs do not vend PII in response bodies, so the raw body
   * is safe to surface and is the most useful field for on-call debugging.
   * (Kept as a prop so a specific surface could opt out if that ever changes.)
   */
  showResponseBody?: boolean;
}

interface DetailRow {
  label: string;
  value?: string;
}

/** Build the ordered list of engineer-facing detail rows (skips empty values). */
const buildDetailRows = (
  exception: ExceptionDetail | undefined,
  context: Partial<FailureContext> | undefined,
  errorCode: string | undefined,
  showResponseBody: boolean
): DetailRow[] => {
  const rows: DetailRow[] = [
    { label: 'Error code', value: exception?.errorCode ?? errorCode },
    { label: 'HTTP status', value: exception?.errorStatus?.toString() },
    { label: 'Transport code', value: exception?.transportCode },
    { label: 'Error type', value: exception?.errorType },
    { label: 'Message', value: exception?.errorMessage },
    { label: 'Request ID', value: exception?.requestId },
    {
      label: 'Retryable',
      value:
        exception?.retryable === undefined
          ? undefined
          : String(exception.retryable)
    },
    { label: 'Source', value: context?.source },
    { label: 'API', value: context?.api },
    { label: 'Session ID', value: context?.sessionId },
    { label: 'Timestamp', value: context?.timestamp },
    // Raw body is PII-sensitive; only included when the caller opts in.
    ...(showResponseBody
      ? [{ label: 'Response body', value: exception?.responseBody }]
      : []),
    { label: 'Component stack', value: context?.componentStack }
  ];
  return rows.filter((row) => row.value !== undefined && row.value !== '');
};

const detailRowsToText = (rows: DetailRow[]): string =>
  rows.map((row) => `${row.label}: ${row.value}`).join('\n');

/**
 * Reusable collapsible "error details" panel for engineers/on-call. Renders the
 * captured {@link ExceptionDetail} fields plus optional page context as labeled
 * monospace rows, with a Copy button. Collapsed by default; renders nothing when
 * there is nothing to show. Used both on the full failure page and inline under
 * modal/banner errors across the admin console.
 */
// React 16 has no useId(); this monotonic counter backs a per-instance ref so
// each mounted panel gets a stable, unique DOM id.
let errorDetailsPanelSeq = 0;

export const ErrorDetailsPanel = ({
  exception,
  context,
  errorCode,
  showResponseBody = true
}: ErrorDetailsPanelProps) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  // A ref pinned once to the next counter value gives each mounted panel a
  // stable, unique DOM id so multiple panels on one page (e.g.
  // FailureErrorView + an inline modal panel) never collide.
  const panelIdRef = useRef<string>();
  if (!panelIdRef.current) {
    panelIdRef.current = `error-details-panel-${(errorDetailsPanelSeq += 1)}`;
  }
  const panelId = panelIdRef.current;
  // Track the "copied" reset timer so it can be cleared on unmount (and before
  // re-arming), avoiding a setState-after-unmount if the panel closes < 2s
  // after a copy.
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(
    () => () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    },
    []
  );
  const rows = buildDetailRows(exception, context, errorCode, showResponseBody);

  if (rows.length === 0) {
    return null;
  }

  const copyDetails = () => {
    const text = detailRowsToText(rows);
    if (navigator?.clipboard?.writeText) {
      void navigator.clipboard.writeText(text).then(
        () => {
          setCopied(true);
          if (copyTimeoutRef.current) {
            clearTimeout(copyTimeoutRef.current);
          }
          copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
        },
        () => {
          /* clipboard denied; no-op, text is still visible on screen */
        }
      );
    }
  };

  return (
    <View
      className={'error-details-wrapper'}
      padding={{ top: 12 }}
      width={'100%'}
    >
      <Col alignItems={'flex-start'}>
        <Button
          variant={ButtonVariant.Tertiary}
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          aria-controls={panelId}
        >
          {expanded ? HIDE_LABEL : SHOW_LABEL}
        </Button>
      </Col>
      {expanded && (
        <View
          id={panelId}
          className={'error-details-panel'}
          padding={16}
          margin={{ top: 12 }}
        >
          <Row
            justifyContent={'space-between'}
            alignItems={'center'}
            padding={{ bottom: 12 }}
          >
            <Text fontSize="T300" fontWeight="bold" color={'neutral90'}>
              Error details
            </Text>
            <Button
              variant={ButtonVariant.Secondary}
              onClick={copyDetails}
              aria-live={'polite'}
            >
              {copied ? COPY_DONE_LABEL : COPY_LABEL}
            </Button>
          </Row>
          <Col gridGap={10}>
            {rows.map((row) => (
              <Col key={row.label} gridGap={2}>
                <Text fontSize="T100" fontWeight="bold" color={'neutral70'}>
                  {row.label}
                </Text>
                <pre className={'error-details-value'}>{row.value}</pre>
              </Col>
            ))}
          </Col>
        </View>
      )}
    </View>
  );
};

export default ErrorDetailsPanel;
